import * as React from 'react';
import { Input } from '@/components/ui/Input';
import { formatPhone } from '@/lib/formatters';

interface PhoneNumberInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'type'
> {
  value: string;
  onChange: (next: string) => void;
}

/**
 * Free-text phone field that always *shows* `XXX-XXX-XXXX` for a 10-digit
 * US number while letting the operator type or paste whatever they have.
 * On blur the stored value is normalized to the same display format so
 * what gets saved matches what's on screen. Values we can't recognise
 * (international, extensions beyond `x123`, partial numbers) pass through
 * untouched — see `formatPhone`.
 *
 * Distinct from `components/auth/PhoneInput`, which normalizes to E.164
 * for the SMS MFA flow.
 */
export const PhoneNumberInput = React.forwardRef<HTMLInputElement, PhoneNumberInputProps>(
  ({ value, onChange, onBlur, placeholder = '555-123-4567', ...props }, ref) => (
    <Input
      ref={ref}
      type="tel"
      inputMode="tel"
      autoComplete="tel"
      placeholder={placeholder}
      value={formatPhone(value)}
      onChange={(e) => onChange(e.target.value)}
      onBlur={(e) => {
        const normalized = formatPhone(value);
        if (normalized !== value) onChange(normalized);
        onBlur?.(e);
      }}
      {...props}
    />
  ),
);
PhoneNumberInput.displayName = 'PhoneNumberInput';
